/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : UpdateDynamicFormTemplate.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import React, { useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomFormTextFieldV2 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";

const UpdateDynamicFormTemplate = ({
  setFields,
  fieldValue,
  setFieldValue,
  fields,
  EDIT_TYPES,
  handleEditField,
  headerOptionsList,
  selectedHeaders,
  signatureOptionsList,
  selectedSignatures,
  footerOptionsList,
  selectedFooters,
  handleHeaderSubTitleChange,
  handleHeaderTitleChange,
  handleFooterTitleChange,
  handleFooterSubTitleChange,
  handleSignatureTitleChange,
  handleSignatureSubTitleChange,
}) => {
  const [isTouched1, setIsTouched1] = useState(false);
  const [isTouched3, setIsTouched3] = useState(false);
  const handleDelete = (sequence) => {
    const newData = fields?.filter((item) => item.sequence !== sequence);
    setFields(newData);
  };

  console.log("fields", fields);

  const updateFieldValue = (prevFields, sequence, newValue) => {
    return (prevFields || []).map((item) => {
      if (item?.sequence === sequence) {
        return { ...item, value: newValue };
      }
      return item;
    });
  };
  const handleFieldChange = (e, field, setFields) => {
    const newValue = e.target.value;
    console.log("Field value changing:", newValue);

    setFields((prevFields) =>
      updateFieldValue(prevFields, field?.sequence, newValue),
    );
  };

  const getFieldColor = (editType, defaultColor) => {
    if (editType === EDIT_TYPES?.DDH) {
      return defaultColor === "blue" ? "blue" : "black";
    }
    if (editType === EDIT_TYPES?.SNOW) {
      return "grey";
    }
    return "black";
  };

  return (
    <Paper
      elevation={2}
      sx={{ border: "1px solid grey", borderRadius: 1, flexGrow: 1 }}
    >
      <Box sx={{ display: "flex", gap: 3 }}>
        {headerOptionsList?.map((item) => (
          <Box
            key={item?.value}
            sx={{
              visibility: selectedHeaders?.includes(item?.value)
                ? "visible"
                : "hidden",

              mt: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                paddingRight: "15px",
                flexDirection: "row",
              }}
            >
              <Box sx={{ flexGrow: 1, marginLeft: "15px" }}>
                {" "}
                <CustomFormTextFieldV2
                  size="small"
                  label="Header Title"
                  required={true}
                  type="text"
                  fldType="text" // 👈 minimum 1 character validation
                  placeholder="Enter Heannnder title"
                  value={item?.title || ""}
                  setValue={(val) => {
                    handleHeaderTitleChange(item?.value, val);
                  }}
                  valueError={
                    isTouched1 && (!item?.title || item?.title.length < 1)
                      ? "Header Title should be at least 1 character"
                      : ""
                  }
                  txtFldStyle={{
                    mt: -1,
                    mb: 1,
                  }}
                  inputProps={{
                    onFocus: () => setIsTouched1(true), // 👈 same as old onFocus
                  }}
                />
                <CustomFormTextFieldV2
                  required
                  size="small"
                  label="Header Subtitle"
                  type="text"
                  fldType="text1" // 👈 minimum 1 character
                  placeholder="Enter Header Subtitle"
                  value={item?.subTitle || ""}
                  setValue={(val) =>
                    handleHeaderSubTitleChange(item?.value, val)
                  }
                  valueError={
                    isTouched1 && (!item?.subTitle || item?.subTitle.length < 1)
                      ? "Header SubTitle should be at least 1 character"
                      : ""
                  }
                  txtFldStyle={{ mb: 1 }}
                  inputProps={{
                    onFocus: () => setIsTouched1(true), // 👈 preserves touched logic
                  }}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
      <Divider sx={{ border: "1px solid lightGrey" }} />
      <Box
        sx={{
          maxHeight: "200px",
          minHeight: "250px",
          m: 2.5,
          mb: 3.4,
          overflowY: "auto",
        }}
      >
        {fields
          ?.slice()
          ?.sort((a, b) => a?.sequence - b?.sequence)
          ?.map((field) => (
            <Box
              key={field?.sequence}
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                mb: 1.4,
              }}
            >
              <Typography
                variant="subtitle1"
                color={getFieldColor(field?.edit_type, "blue")}
              >
                {`${field?.sequence}. ${field.field_name}: `}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: -1,
                }}
              >
                <TextField
                  fullWidth
                  size="large"
                  variant="standard"
                  type={field?.default_value}
                  value={field?.value || fieldValue}
                  onChange={(e) => handleFieldChange(e, field, setFields)}
                  sx={{ ml: 1 }}
                  disabled
                />
                <IconButton
                  size="small"
                  onClick={() => handleEditField(field?.sequence)}
                >
                  <EditIcon
                    sx={{
                      fontSize: "1rem",
                      color: getFieldColor(field?.edit_type, "black"),
                    }}
                  />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDelete(field?.sequence)}
                >
                  <DeleteIcon sx={{ color: "black", fontSize: "1rem" }} />
                </IconButton>
              </Box>
            </Box>
          ))}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          gap: 4,
        }}
      >
        {signatureOptionsList?.map((item) => (
          <Box
            key={item?.value}
            sx={{
              visibility: selectedSignatures?.includes(item?.value)
                ? "visible"
                : "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center", // Vertically aligns items
                paddingRight: "15px",
              }}
            >
              <Box sx={{ flexGrow: 1, marginLeft: "15px" }}>
                {" "}
                <CustomFormTextFieldV2
                  required
                  size="small"
                  label="Signature Title"
                  placeholder="Enter Signature title"
                  type="text"
                  fldType="text1" // 👈 minimum 1 character
                  value={item?.title || ""}
                  setValue={(val) =>
                    handleSignatureTitleChange(item?.value, val)
                  }
                  txtFldStyle={{
                    mt: -1,
                    mb: 1,
                    width: "270px",
                  }}
                />
                <CustomFormTextFieldV2
                  required
                  size="small"
                  label="Signature Subtitle"
                  placeholder="Enter Signature subtitle"
                  type="text"
                  fldType="text1" // 👈 minimum 1 character
                  value={item?.subTitle || ""}
                  setValue={(val) =>
                    handleSignatureSubTitleChange(item?.value, val)
                  }
                  txtFldStyle={{
                    mb: 1,
                    width: "270px",
                  }}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
      <Divider sx={{ border: "1px solid lightGrey" }} />
      <Box sx={{ display: "flex", gap: 2, mt: "10px" }}>
        {footerOptionsList?.map((item) => (
          <Box
            key={item?.value}
            sx={{
              visibility: selectedFooters?.includes(item?.value)
                ? "visible"
                : "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                paddingRight: "15px",
              }}
            >
              <Box sx={{ flexGrow: 1, marginLeft: "15px" }}>
                {" "}
                <CustomFormTextFieldV2
                  required
                  fullWidth
                  size="small"
                  label="Footer Title"
                  placeholder="Enter Footer title"
                  type="text"
                  fldType="text1" // 👈 minimum 1 character
                  value={item?.title || ""}
                  setValue={(val) => handleFooterTitleChange(item?.value, val)}
                  valueError={
                    isTouched3 && (!item?.title || item?.title.length < 1)
                      ? "Footer Title should be at least 1 character"
                      : ""
                  }
                  txtFldStyle={{
                    mt: -1,
                    mb: 1,
                    fontWeight: "bold",
                  }}
                  inputProps={{
                    onFocus: () => setIsTouched3(true), // 👈 touched logic
                  }}
                />
                <CustomFormTextFieldV2
                  required
                  fullWidth
                  size="small"
                  label="Footer Subtitle"
                  placeholder="Enter Footer Subtitle"
                  type="text"
                  fldType="text1" // 👈 minimum 1 character
                  value={item?.subTitle || ""}
                  setValue={(val) =>
                    handleFooterSubTitleChange(item?.value, val)
                  }
                  valueError={
                    isTouched3 && (!item?.subTitle || item?.subTitle.length < 1)
                      ? "Footer SubTitle should be at least 1 character"
                      : ""
                  }
                  maxLength={100}
                  txtFldStyle={{
                    mb: 1,
                  }}
                  inputProps={{
                    onFocus: () => setIsTouched3(true),
                  }}
                />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

UpdateDynamicFormTemplate.propTypes = {
  setFields: PropTypes.func,
  fieldValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setFieldValue: PropTypes.func,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      sequence: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      field_name: PropTypes.string,
      edit_type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      default_value: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
  EDIT_TYPES: PropTypes.shape({
    DDH: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    SNOW: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  handleEditField: PropTypes.func,
  headerOptionsList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      subTitle: PropTypes.string,
    }),
  ),
  selectedHeaders: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  signatureOptionsList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      subTitle: PropTypes.string,
    }),
  ),
  selectedSignatures: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  footerOptionsList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      subTitle: PropTypes.string,
    }),
  ),
  selectedFooters: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  handleHeaderSubTitleChange: PropTypes.func,
  handleHeaderTitleChange: PropTypes.func,
  handleFooterTitleChange: PropTypes.func,
  handleFooterSubTitleChange: PropTypes.func,
  handleSignatureTitleChange: PropTypes.func,
  handleSignatureSubTitleChange: PropTypes.func,
};

export default UpdateDynamicFormTemplate;


